import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";
import { environment } from "../../../environments/environment";

const STORAGE_KEY = "staffBranchIds";

interface BranchScopeCache {
  userId: number;
  ids: number[] | null;
}

interface BranchRow {
  id?: number | string;
}

@Injectable({ providedIn: "root" })
export class BranchScopeService {
  private readonly http = inject(HttpClient);
  private ids: number[] | null | undefined;
  private fetchedForUser: number | null = null;
  private pending: Observable<number[] | null> | null = null;

  constructor() {
    const cached = this.readCache();
    if (cached && cached.userId === this.currentUserId()) {
      this.ids = cached.ids;
    }
  }

  ensure(): Observable<number[] | null> {
    const userId = this.currentUserId();
    if (userId == null) {
      this.ids = null;
      this.fetchedForUser = null;
      return of(null);
    }
    if (this.fetchedForUser === userId && this.ids !== undefined) {
      return of(this.ids);
    }
    if (this.pending) return this.pending;

    this.pending = this.http
      .get<any>(`${environment.apiUrl}/users/${userId}/branches`)
      .pipe(
        map((res) => this.parseIds(res)),
        catchError(() => of(null)),
        tap((ids) => {
          this.ids = ids;
          this.fetchedForUser = userId;
          this.pending = null;
          this.writeCache({ userId, ids });
        }),
        shareReplay(1),
      );
    return this.pending;
  }

  allowGlobal(): boolean {
    return !Array.isArray(this.ids);
  }

  peek(): number[] | null | undefined {
    return this.ids;
  }

  filterItems<T>(
    items: T[],
    ids: number[] | null,
    getId: (item: T) => number | string | null | undefined,
  ): T[] {
    if (!ids?.length) return items;
    const allowed = new Set(ids);
    return items.filter((item) => allowed.has(Number(getId(item))));
  }

  applyToResponse(res: any, ids: number[] | null): any {
    if (!ids?.length) return res;
    if (Array.isArray(res)) {
      return this.filterItems<BranchRow>(res, ids, (row) => row.id);
    }
    const data = res?.data as BranchRow[] | { items?: BranchRow[]; total?: number } | undefined;
    if (Array.isArray(data)) {
      return { ...res, data: this.filterItems(data, ids, (row) => row.id) };
    }
    if (data && Array.isArray(data.items)) {
      const items = this.filterItems(data.items, ids, (row) => row.id);
      return {
        ...res,
        data: {
          ...data,
          items,
          ...(typeof data.total === "number" ? { total: items.length } : {}),
        },
      };
    }
    return res;
  }

  clear(): void {
    this.ids = undefined;
    this.fetchedForUser = null;
    this.pending = null;
    localStorage.removeItem(STORAGE_KEY);
  }

  private parseIds(res: any): number[] | null {
    const items = Array.isArray(res?.data?.items)
      ? res.data.items
      : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
    const ids = items
      .map((item: any) => Number(item?.id))
      .filter((id: number) => Number.isFinite(id));
    return ids.length > 0 ? ids : null;
  }

  private currentUserId(): number | null {
    const raw = localStorage.getItem("userId");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }

  private readCache(): BranchScopeCache | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      const userId = Number(parsed.userId);
      if (!Number.isFinite(userId)) return null;
      if (parsed.ids === null) return { userId, ids: null };
      if (!Array.isArray(parsed.ids)) return null;
      return {
        userId,
        ids: parsed.ids.map(Number).filter((id: number) => Number.isFinite(id)),
      };
    } catch {
      return null;
    }
  }

  private writeCache(cache: BranchScopeCache): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  }
}
